import { fetchShopDeliveryInfo } from './shopFreeShipping';

export const FREE_SHIP_FALLBACK = 100;

/**
 * Reads the store’s free-shipping floor from GET /orders/get-shipping-cost (optionally ?businessId=).
 * Falls back to FREE_SHIP_FALLBACK when the request fails.
 */
export async function getFreeShippingThresholdFromApi({ apiBaseV1, businessId }) {
  const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId });
  return d.freeThreshold;
}

/**
 * Server-side shop shipping quote (aligns with POST /api/v1/orders/quote-shipping).
 */
export async function quoteShippingFromCart({
  apiBaseV1,
  businessId,
  cartItems,
  getTotalPrice,
  deliveryMode = 'ship',
  shippingAddress,
}) {
  const PHYSICAL_TYPES = ['physical', 'print_on_demand', 'bundle', 'rental'];
  const hasPhysical = (cartItems || []).some(
    (item) => PHYSICAL_TYPES.includes(item.type) || PHYSICAL_TYPES.includes(item.productType)
  );
  if (!hasPhysical) {
    return {
      shippingCost: 0,
      freeThreshold: FREE_SHIP_FALLBACK,
      showFreeShippingPromo: true,
      useDefaultFreeShippingThreshold: true,
      subtotalFreeShippingEnabled: true,
      pickupEligible: false,
    };
  }
  const subtotalAll = typeof getTotalPrice === 'function' ? getTotalPrice() : 0;
  const productItems = (cartItems || [])
    .filter((i) => !(i.type === 'event' || i.eventId))
    .map((i) => ({
      productId: i.productId || i.id,
      quantity: i.quantity || 1,
      variantId: i.variantId || undefined,
    }))
    .filter((row) => Boolean(row.productId));
  if (!businessId || productItems.length === 0) {
    const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId: (businessId || '').trim() });
    return {
      shippingCost: 10,
      freeThreshold: d.freeThreshold,
      showFreeShippingPromo: d.showFreeShippingPromo,
      useDefaultFreeShippingThreshold: d.useDefaultFreeShippingThreshold,
      subtotalFreeShippingEnabled: d.subtotalFreeShippingEnabled !== false,
      pickupEligible: false,
    };
  }
  const res = await fetch(`${apiBaseV1}/orders/quote-shipping`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      businessId,
      deliveryMode,
      orderSubtotal: subtotalAll,
      items: productItems,
      shippingAddress:
        deliveryMode === 'pickup'
          ? undefined
          : {
              country: (shippingAddress && shippingAddress.country) || 'Australia',
              state: shippingAddress && shippingAddress.state,
              postalCode: shippingAddress && shippingAddress.postalCode,
              line1: shippingAddress && shippingAddress.street,
              city: shippingAddress && shippingAddress.city,
            },
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    const d = await fetchShopDeliveryInfo({ apiBaseV1, businessId: (businessId || '').trim() });
    return {
      shippingCost: 10,
      freeThreshold: d.freeThreshold,
      showFreeShippingPromo: d.showFreeShippingPromo,
      useDefaultFreeShippingThreshold: d.useDefaultFreeShippingThreshold,
      subtotalFreeShippingEnabled: d.subtotalFreeShippingEnabled !== false,
      pickupEligible: false,
    };
  }
  return {
    shippingCost: Number(data.shippingCost) || 0,
    freeThreshold: Number(data.freeThreshold) || FREE_SHIP_FALLBACK,
    pickupEligible: !!data.pickupEligible,
    showFreeShippingPromo: data.showFreeShippingPromo !== false,
    useDefaultFreeShippingThreshold: data.useDefaultFreeShippingThreshold !== false,
    subtotalFreeShippingEnabled: data.subtotalFreeShippingEnabled !== false,
  };
}
